import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import Script from 'next/script';
import ErrorPage from 'next/error';
import '/public/css/pages/weather.css';
import '/public/css/components/apmGallerySlideshow.css';
import Link from 'components/AppSafeLink/AppSafeLink';
import { Image } from '@apmg/mimas';
import ErrorBoundary from '../../components/ErrorBoundary/ErrorBoundary';
import { Teaser } from '@apmg/titan';
import Loading from 'components/Loading/Loading';
import '/public/css/components/fancySlider.css';
import '/public/css/components/scrollmenu.css';
import { fetchWeather } from '../../utils/fetchWeather';
import { weatherConfig } from '../../utils/defaultData';
import {
  fetchMemberDriveStatus,
  addMemberDriveElements,
} from '../../utils/membershipUtils';
import adCleanup from '../../utils/adCleanup';
import gqlquery from '../../endpoints/Weather/weather.gql';
import CollectionQuery from 'models/Collection/CollectionQuery.ts';
import fetcher from '../../utils/fetcher';
import { CtoF, degToCompass, getSunriseSunset } from '../../utils/utils';
import dayjs from 'dayjs';
import WeatherAlert from '../../components/WeatherAlert/WeatherAlert';
import TwoDaysChart from '../../endpoints/Weather/TwoDaysChart';
import SevenDay from 'components/Weather/SevenDay';
import AudioPlayButton from 'components/AudioPlayButton/AudioPlayButton';
import AddFavoriteWrapper from 'components/Data/AddFavoriteWrapper';
import AdvertisementBanner from 'components/Advertisement/AdvertisementBanner';
import { globals } from '../../config/globals';
import NewsLetterSideBar from 'components/NewsLetterSideBar/NewsLetterSideBar';
import { Body } from '@apmg/amat';
import { linkByTypeAs } from 'utils/cjsutils';
import relativeTime from 'dayjs/plugin/relativeTime';
import WeatherFacts from 'endpoints/Weather/WeatherFacts';
import AdCategoriesContext from 'context/AdCategoriesContext';
import Metatags from 'components/Metatags/Metatags';
dayjs.extend(relativeTime);

const WeatherPage = ({ data, errorCode }) => {
  const [weatherData, setWeatherData] = useState(data);
  const [loading, setLoading] = useState(true);
  const [weatherKit, setWeatherKit] = useState();
  const [city, setCity] = useState(data.location.city);
  const updraftStory =
    weatherData?.cmsData?.updraft?.results?.items?.[0] &&
    weatherData.cmsData.updraft.results.items[0];
  const weathernewsStory = data?.weathernews?.collection?.results?.items[0];
  const climatecaststories = data?.climatecast?.collection?.results?.items;
  const context = useContext(AdCategoriesContext);

  if (errorCode) return <ErrorPage statusCode={errorCode} />;

  const handleChangeSearchCity = (event) => {
    setCity(event.target.value);
  };

  const handleSubmitSearchCity = async (event) => {
    event.preventDefault();

    const response = await fetch(
      `https://api.opencagedata.com/geocode/v1/json?q=${city}&key=885cb15f436744d68d85ad1d06e1b5f4`
    );

    const res = await response.json();

    const cityortown = res?.results[0]?.components?.town
      ? res?.results[0]?.components.town
      : res?.results[0]?.components.city;
    window.location.href = `/weather?latitude=${res.results[0].geometry.lat}&longitude=${res.results[0].geometry.lng}&city=${cityortown}`;
  };

  const getWeatherKitData = async () => {
    const method = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const currentWeatherRequest = await fetch(
      `api/weatherKit/currentweather/${data.location.latitude}/${data.location.longitude}`,
      method
    );
    const forecastdailyRequest = await fetch(
      `api/weatherKit/forecastdaily/${data.location.latitude}/${data.location.longitude}`,
      method
    );

    const currentWeatherResponse = await currentWeatherRequest.json();
    const forecastDailyResponse = await forecastdailyRequest.json();

    setWeatherKit({
      currentWeatherResponse,
      forecastDailyResponse,
    });
  };

  const getMetWeatherData = async () => {
    let weather, forecast, alerts;

    try {
      ({ weather, forecast, alerts } = await fetchWeather(
        data.location.latitude,
        data.location.longitude
      ));
    } catch (err) {
      console.error(err);
    }

    setWeatherData((prevState) => {
      return {
        ...prevState,
        location: data.location,
        weather,
        forecast,
        alerts,
      };
    });
    setLoading(false);
  };
  useEffect(() => {
    context.setPrimaryCategory('weather');
    context.setAdCategories(['weather']);
    getWeatherKitData();
    getMetWeatherData().then(() => {
      fetchMemberDriveStatus().then((data) => {
        addMemberDriveElements(data);
      });
    });
  }, []);

  useEffect(() => {
    if (window.wxWidgets) window.wxWidgets.parse();
  });

  const weatherKitLat =
    weatherKit?.currentWeatherResponse?.currentWeather?.metadata?.latitude &&
    weatherKit.currentWeatherResponse.currentWeather.metadata.latitude;
  const weatherKitLng =
    weatherKit?.currentWeatherResponse?.currentWeather?.metadata?.longitude &&
    weatherKit.currentWeatherResponse.currentWeather.metadata.longitude;

  let sunTimes =
    weatherKitLat &&
    weatherKitLng &&
    getSunriseSunset(weatherKitLat, weatherKitLng);

  const weatherFacts =
    weatherData?.cmsData?.weatherFacts?.json &&
    JSON.parse(weatherData.cmsData.weatherFacts.json);

  const sevenDay =
    weatherKit?.forecastDailyResponse?.forecastDaily?.days &&
    weatherKit.forecastDailyResponse.forecastDaily.days.slice(0, 7);

  let adUnitPathUrl = globals.adUnitPath;

  const adInsert = {
    type: 'adinsert',
    id: `mpr-ad-2`,
    adUnitPath: `${adUnitPathUrl}/weather`,
    sizes: [[300, 250]],
    sizeMap: { 0: [300, 250] },
    cmsId: 'sidebar_ad',
    primaryCollection: 'sidebar_ad',
    collections: ['sidebar_ad'],
    position: 'mr_bottom',
    isSidebar: true,
  };

  return (
    <ErrorBoundary>
      <Metatags fullSlug="weather" title="Weather" />
      <div className="weather wrapper">
        <div id="mainContent"></div>
        <div className="container">
          <div className="premium-underwriting">
            <AdvertisementBanner
              id="premium_ad"
              primaryCollection="weather"
              sizes={[[320, 25]]}
              sizeMap={{ 0: [320, 25] }}
              customCss="underwriting"
            />
          </div>
          <h1 className="type-caps type-center">Weather</h1>
          <p className="type-center type-balance">
            Your trusted source for timely and accurate weather updates.
          </p>
          {weatherData.alerts &&
            weatherData.alerts.map((alert) => {
              return <WeatherAlert key={alert.id} alert={alert} />;
            })}
          <div className="weather-inner">
            <div className="weather-date type-sm">
              {new Intl.DateTimeFormat('en-US', {
                dateStyle: 'full',
              }).format(new Date())}
            </div>
            <div className="weather-form">
              <form
                onSubmit={handleSubmitSearchCity}
                aria-label="Weather location"
                className="type-sm"
              >
                <input
                  type="text"
                  placeholder={city}
                  onChange={handleChangeSearchCity}
                />
                <input type="submit" value="📍" />
              </form>
            </div>
            {weatherKit?.currentWeatherResponse?.currentWeather ? (
              <section key="currentConditions" className="weather-current">
                <>
                  <h2 className="weather-current-header type-sm type-caps">
                    Current Conditions
                  </h2>
                  <div className="weather-current-icon">
                    {weatherKit &&
                      weatherKit?.currentWeatherResponse?.currentWeather
                        ?.conditionCode && (
                        <img
                          src={`/siteicons/weatherkitIcons/svg/${weatherKit.currentWeatherResponse.currentWeather.conditionCode.toLowerCase()}.svg`}
                          alt={
                            weatherKit.currentWeatherResponse.currentWeather
                              .conditionCode
                          }
                        />
                      )}
                  </div>
                  <div className="weather-current-temp">
                    {weatherKit &&
                      CtoF(
                        weatherKit.currentWeatherResponse.currentWeather
                          .temperature
                      )}
                    <span className="type-minisymbol">°F</span>
                  </div>
                  <div className="weather-current-location">{city}</div>
                  <div className="weather-current-high-low type-3x">
                    <span className="type-minisymbol">↑</span>
                    {weatherKit &&
                      CtoF(
                        weatherKit.forecastDailyResponse.forecastDaily.days[0]
                          .temperatureMax
                      )}
                    <span className="type-minisymbol">°F</span>
                    &emsp;
                    <span className="type-minisymbol">↓</span>
                    {weatherKit &&
                      CtoF(
                        weatherKit.forecastDailyResponse.forecastDaily.days[0]
                          .temperatureMin
                      )}
                    <span className="type-minisymbol">°F</span>
                  </div>
                </>
              </section>
            ) : (
              <Loading white />
            )}
            <section className="weather-updraft">
              <h2 className="type-lg">
                <span className="type-slug">Latest</span> Updraft
                <span className="type-minisymbol">®</span>
              </h2>
              {updraftStory && (
                <div className="weather-updraft-teaser">
                  <Teaser
                    id={updraftStory.id}
                    title={updraftStory.title}
                    href={linkByTypeAs(updraftStory)}
                    headingLevel={3}
                    description={updraftStory.descriptionText}
                  />
                </div>
              )}
              <Link
                href="/weather-and-climate/updraft"
                className="btn btn-primary btn-slim weather-btn-charcoal"
                passHref
              >
                More from Updraft®
              </Link>
            </section>
            {sevenDay ? <SevenDay data={sevenDay} /> : <Loading white />}
            <section key="radar" id="radar" className="weather-radar">
              <ErrorBoundary>
                <Script src="https://widgets.media.weather.com/wxwidget.loader.js?cid=431513429" />
                <>
                  <h2 className="weather-radar-header type-sm type-caps">
                    Interactive Radar
                  </h2>
                  <wx-config
                    latitude={
                      weatherData.location.latitude || weatherConfig[0].lat
                    }
                    longitude={
                      weatherData.location.longitude || weatherConfig[0].lng
                    }
                    urlconfig="true"
                    group="true"
                  ></wx-config>
                  <wx-widget
                    type="map"
                    mapid="0001"
                    group="true"
                    zoomlevel="6.5"
                    standalone="true"
                  ></wx-widget>
                </>
              </ErrorBoundary>
            </section>
            <section key="forecast" className="weather-hourly">
              {weatherData?.forecast && (
                <TwoDaysChart forecast={weatherData.forecast} />
              )}
            </section>
            {weatherData?.weather && (
              <section className="weather-outside">
                <h2 className="type-sm type-caps">A Look Outside</h2>
                <ul className="weather-outside-block">
                  <li className="weather-outside-entry">
                    <div className="weather-outside-label">Humidity</div>
                    <div className="weather-outside-icon">
                      <img
                        src="/siteicons/weatherinfotypes/humidity.svg"
                        alt="Humidity" /* decorative */
                      />
                    </div>
                    <div className="weather-outside-data">
                      {weatherData &&
                        weatherData.weather &&
                        Math.round(weatherData.weather.relativeHumidity)}
                      <span className="type-minisymbol">%</span>
                    </div>
                  </li>
                  <li className="weather-outside-entry">
                    <div className="weather-outside-label">Air Pressure</div>
                    <div className="weather-outside-icon">
                      <img
                        src="/siteicons/weatherinfotypes/airpressure.svg"
                        alt="Air Pressure" /* decorative */
                      />
                    </div>
                    <div className="weather-outside-data">
                      {weatherData &&
                        weatherData.weather &&
                        (weatherData.weather.pressure / 33.8).toFixed(2)}
                      <span className="type-minisymbol">inHg</span>
                    </div>
                  </li>
                  <li className="weather-outside-entry">
                    <div className="weather-outside-label">Precipitation</div>

                    <div className="weather-outside-icon">
                      <img
                        src="/siteicons/weatherinfotypes/precipitation.svg"
                        alt="Percent chance precipitation" /* decorative */
                      />
                    </div>
                    <div className="weather-outside-data">
                      {sevenDay &&
                        Math.round(sevenDay[0].precipitationChance * 100)}
                      <span className="type-minisymbol">%</span>
                    </div>
                  </li>
                  <li className="weather-outside-entry">
                    <div className="weather-outside-label">Visibility</div>
                    <div className="weather-outside-icon">
                      <div className="weather-outside-icon">
                        <img
                          src="/siteicons/weatherinfotypes/visibility.svg"
                          alt="Visibility" /* decorative */
                        />
                      </div>
                    </div>
                    <div className="weather-outside-data">
                      {weatherKit &&
                        (
                          weatherKit.currentWeatherResponse.currentWeather
                            .visibility / 1760
                        ).toPrecision(2)}
                      <span className="type-minisymbol">mi</span>
                    </div>
                  </li>
                  <li className="weather-outside-entry">
                    <div className="weather-outside-label">Wind</div>
                    <div className="weather-outside-icon">
                      <img
                        src="/siteicons/weatherinfotypes/wind.svg"
                        alt="Wind Speed" /* decorative */
                      />
                    </div>
                    <div className="weather-outside-data">
                      {weatherKit &&
                        degToCompass(
                          weatherKit.currentWeatherResponse.currentWeather
                            .windDirection
                        )}{' '}
                      {weatherKit &&
                        Math.round(
                          weatherKit.currentWeatherResponse.currentWeather
                            .windSpeed
                        )}
                      <span className="type-minisymbol">MPH</span>
                    </div>
                  </li>
                  <li className="weather-outside-entry">
                    <div className="weather-outside-label">Sunrise</div>
                    <div className="weather-outside-icon">
                      <img
                        src="/siteicons/weatherinfotypes/sunrise.svg"
                        alt="Sunrise" /* decorative */
                      />
                    </div>
                    <div className="weather-outside-data">
                      {sunTimes && sunTimes.sunrise}
                    </div>
                  </li>
                  <li className="weather-outside-entry">
                    <div className="weather-outside-label">Sunset</div>
                    <div className="weather-outside-icon">
                      <img
                        src="/siteicons/weatherinfotypes/sunset.svg"
                        alt="Sunset" /* decorative */
                      />
                    </div>
                    <div className="weather-outside-data">
                      {sunTimes && sunTimes.sunset}
                    </div>
                  </li>
                </ul>
              </section>
            )}
          </div>
        </div>
      </div>
      <div className="container">
        <section className="weather-news">
          <div className="grid grid-4col grid-with-border">
            <h2 className="hdg-block">Weather News</h2>
          </div>
          <div className="grid weather-stories">
            <div className="weather-main-story">
              <article>
                <div>
                  {weathernewsStory &&
                    weathernewsStory?.primaryVisuals?.thumbnail && (
                      <>
                        <Link
                          href={linkByTypeAs(weathernewsStory)}
                          key={weathernewsStory.canonicalSlug}
                          passHref={true}
                        >
                          <Image
                            image={weathernewsStory.primaryVisuals.thumbnail}
                            aspectRatio="thumbnail"
                            sizes="(min-width: 72em) 50vw,  80vw"
                          />
                        </Link>
                        <div className="weather-story-date">
                          {dayjs(weathernewsStory.publishDate).fromNow()}
                        </div>
                        <Link
                          href={linkByTypeAs(weathernewsStory)}
                          passHref={true}
                        >
                          <h2>{weathernewsStory.title}</h2>
                          <p>{weathernewsStory.descriptionText}</p>
                        </Link>
                      </>
                    )}
                </div>
                {weathernewsStory &&
                  weathernewsStory?.primaryAudio?.encodings?.[0]
                    ?.playFilePath && (
                    <div className="weather-story-controls-bottom">
                      <AudioPlayButton
                        audioTitle={weathernewsStory.title}
                        label="Listen"
                        audioSource={
                          weathernewsStory.primaryAudio.encodings[0]
                            .playFilePath
                        }
                        audioThumbnailSrc={
                          weathernewsStory.primaryVisuals.lead?.aspect_ratios
                            .square?.instances[0].url
                        }
                        audioThumbnailAlt={
                          weathernewsStory.primaryVisuals?.lead?.shortCaption ||
                          weathernewsStory.title
                        }
                        showDuration={`${
                          weathernewsStory.primaryAudio.encodings[0]
                            .durationHms || ''
                        }`}
                      />
                      <AddFavoriteWrapper
                        title={weathernewsStory.title}
                        contentArea="mprnews"
                        resourceType={weathernewsStory.resourceType}
                        id={weathernewsStory.id}
                        slug={weathernewsStory.canonicalSlug}
                      />
                    </div>
                  )}
              </article>
            </div>
            <div className="weather-ask-sven-paul">
              <div className="weather-ask-sven-paul-portraits">
                <img
                  src="/img/meteorologists.jpg"
                  alt="MPR News meteorologists Ben Cathey, Sven Sundgaard and Mandy Thalhuber"
                  height="450"
                  width="800"
                />
              </div>
              <h3 className="hdg-4">
                Ask Ben, Mandy and Sven &mdash; MPR News meteorologists answer
                your weather- and climate-related questions
              </h3>
              <p>
                Do you have a weather-related question that you would like
                answered?
              </p>
              <p>
                MPR News Meteorologists Ben Cathey, Sven Sundgaard and Mandy
                Thalhuber answer your questions.
              </p>
              <p>
                <Link
                  href="https://modules.wearehearken.com/mpr/embed/11748/share"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Questions for Ben, Mandy and Sven — Link opens in new tab"
                  className="btn btn-primary"
                >
                  Questions and Answers
                </Link>
              </p>
            </div>
            <div className="weather-news-sidebar">
              <AdvertisementBanner {...adInsert} />
              <NewsLetterSideBar showZipCode />
            </div>
          </div>
        </section>
      </div>
      {data?.cmsData?.photostory?.body && (
        <div className="fancy-slider weather-fancy-slider">
          <div className="container">
            <Body nodeData={JSON.parse(data.cmsData.photostory.body)} />
          </div>
        </div>
      )}
      <div className="container">
        <section className="weather-climate-cast">
          <div className="grid grid-4col grid-with-border">
            <h2 className="hdg-block">Climate Cast®</h2>
          </div>
          <ul className="weather-stories grid grid-3col">
            {climatecaststories &&
              climatecaststories?.slice(0, 3).map((item, i) => {
                return (
                  <li key={i} className="weather-climate-cast-story">
                    <div className="weather-story-date">
                      {dayjs(item.publishDate).format('MMMM D')}
                    </div>
                    {item.primaryVisuals?.thumbnail?.aspect_ratios?.normal && (
                      <Link href={linkByTypeAs(item)} passHref>
                        <Image
                          image={item.primaryVisuals.thumbnail}
                          sizes="(min-width: 72em) 20vw,  80vw"
                          aspectRatio="normal"
                        />
                      </Link>
                    )}
                    <div className="weather-story-text">
                      <Link href={linkByTypeAs(item)} passHref>
                        <h3 className="hdg-4">{item.title}</h3>
                        <p className="weather-climate-cast-description type-sm">
                          {item.description}
                        </p>
                      </Link>
                    </div>
                  </li>
                );
              })}
          </ul>
          <p className="weather-climate-cast-view-all-btn">
            <Link href="/podcasts/climate-cast" className="btn btn-primary">
              More from Climate Cast®
            </Link>
          </p>
        </section>
      </div>
      {weatherFacts?.categories && <WeatherFacts weatherFacts={weatherFacts} />}
    </ErrorBoundary>
  );
};

WeatherPage.getInitialProps = async ({ query }) => {
  let errorCode;
  const results = await Promise.all([
    fetcher(gqlquery, {
      contentAreaSlug: process.env.CONTENT_AREA_SLUG,
      slug: 'weather-and-climate/updraft',
      pageNum: parseInt(1),
      pageSize: parseInt(10),
    }),
    fetcher(CollectionQuery, {
      contentAreaSlug: process.env.CONTENT_AREA_SLUG,
      slug: 'weather-and-climate',
      pageNum: parseInt(1),
      pageSize: parseInt(10),
    }),
    fetcher(CollectionQuery, {
      contentAreaSlug: process.env.CONTENT_AREA_SLUG,
      slug: 'podcasts/climate-cast',
      pageNum: parseInt(1),
      pageSize: parseInt(10),
    }),
  ]);

  adCleanup();
  return {
    resourceType: 'weather',
    layout: 'weather',
    data: {
      cmsData: results[0],
      weathernews: results[1],
      climatecast: results[2],
      location: {
        id: query.city ? query.city.toLowerCase() : 'msp',
        city: query?.city || 'Twin Cities',
        latitude: query.latitude || weatherConfig[1].lat,
        longitude: query.longitude || weatherConfig[1].lng,
      },
    },

    errorCode,
  };
};

WeatherPage.propTypes = {
  errorCode: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  data: PropTypes.object,
};

export default WeatherPage;
